import { Rules as de_Rules } from "./de/Rules";
import { Rules as enGB_Rules } from "./en-GB/Rules";
import { Rules as es_Rules } from "./es/Rules";
import { Rules as hi_Rules } from "./hi/Rules";
import { Rules as ja_Rules } from "./ja/Rules";
import { Rules as zh_Rules } from "./zh/Rules";

import { ILabel, ILabels, ILabelsParser, ILanguageRules, IModule } from "./Interfaces";
import { LabelsHelper } from "./LabelsHelper";
import { LanguageOptions } from "./LanguageOptions";
import { PoEditorLabelsParser } from "./PoEditorLabelsParser";
import { Localisation } from "./Localisation";

export class LabelBuilder<TLabels extends ILabels>
{
    private localisation: Localisation;
    public labels: TLabels;
    public languageOptions!: LanguageOptions;
    public labelsProviders: ((language: string) => Promise<IModule>)[] = [];
    public labelsParser!: ILabelsParser;
    public autoMapEnabled = false;

    constructor(localisation: Localisation, labels: TLabels)
    {
        this.localisation = localisation;
        this.labels = labels;
    }

    public setLangugeOptions(languageOptions: LanguageOptions): LabelBuilder<TLabels>
    {
        this.languageOptions = languageOptions;
        return this;
    }

    public addLabelsProvider(labelsProvider: (language: string) => Promise<IModule>): LabelBuilder<TLabels>
    {
        this.labelsProviders.push(labelsProvider);
        return this;
    }

    public usePoEditorLabelsParser(): LabelBuilder<TLabels>
    {
        this.labelsParser = new PoEditorLabelsParser();
        return this;
    }

    public autoMap(): LabelBuilder<TLabels>
    {
        this.autoMapEnabled = true;
        return this;
    }

    public async build(): Promise<LabelBuilder<TLabels>>
    {
        const currentLanguage = this.languageOptions.current;
        let labels = new Map<string, ILabel>();

        if (currentLanguage != this.localisation.terms)
        {
            for (const labelsProvider of this.labelsProviders)
            {
                try
                {
                    const module = await labelsProvider(currentLanguage);
                    labels = await this.labelsParser.parse(module.default);
                    console.log(`Loaded labels for '${currentLanguage}'.`);
                    break;
                }
                catch
                {
                    console.error(`Failed to load labels for '${currentLanguage}'.`);
                }
            }
        }

        const rules = this.getRules();
        this.labels.helper = new LabelsHelper(labels, rules);

        if (this.autoMapEnabled)
        {
            this.mapLabels();
        }

        return this;
    }

    private getRules(): ILanguageRules
    {
        let currentLanguage = this.languageOptions.current;
        let rules: ILanguageRules;

        switch (currentLanguage)
        {
            case 'de':
                rules = new de_Rules();
                break;

            case 'en-GB':
            case this.localisation.terms:
                rules = new enGB_Rules();
                break;

            case 'es':
                rules = new es_Rules();
                break;

            case 'hi':
                rules = new hi_Rules();
                break;

            case 'ja':
                rules = new ja_Rules();
                break;

            case 'zh':
                rules = new zh_Rules();
                break;

            default:
                console.error(`Language rules for '${currentLanguage}' not supported.`);
                currentLanguage = 'en-GB'
                rules = new enGB_Rules();
                break;
        }

        console.log(`Using language rules for '${currentLanguage}'.`);
        return rules;
    }

    private mapLabels(): void
    {
        const labels = this.labels;
        const prototype = Object.getPrototypeOf(labels);
        const methodNames = Object.getOwnPropertyNames(prototype).filter(i => i != 'constructor');
        const propertyNames = Object.keys(labels).filter(i => i != 'helper');

        for (const key of propertyNames)
        {
            (labels as any)[key] = labels.helper.value(key);
        }

        for (const key of methodNames)
        {
            const descriptor = Object.getOwnPropertyDescriptor(prototype, key);
            if (descriptor?.get)
            {
                Object.defineProperty(labels, key, {
                    get: () => labels.helper.value(key),
                    configurable: true,
                    enumerable: true,
                });
            }
            else if (typeof descriptor?.value == 'function')
            {
                const parameterNames = this.getLabelParameters(descriptor.value);

                Object.defineProperty(labels, key, {
                    value: (...args: any[]) => labels.helper.value(key, ...parameterNames.map((name, index) => ({ name: name, value: args[index] }))),
                    writable: true,
                });
            }
        }
    }

    private getLabelParameters(func: Function): string[]
    {
        const parameterNamesAsString = func.toString().match(/^\s*\w+\s*\(([^)]*)\)/)?.[1] ?? '';

        const parameterNames = parameterNamesAsString
            .split(',')
            .map(i => i.trim())
            .filter(i => i.length > 0);

        return parameterNames;
    }
}
